import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faPencil,
  faTrash,
  faMagnifyingGlass,
  faFilter,
  faBuilding,
  faEye,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import InlineFieldTooltip from "../components/InlineFieldTooltip";

const Bridges = () => {
  const navigate = useNavigate();
  const { isPlanner } = useAuth();
  const [bridges, setBridges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [togglingBridgeId, setTogglingBridgeId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingBridge, setEditingBridge] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    isActive: "",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalBridges: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const [bridgeTypes, setBridgeTypes] = useState([]);
  const [formData, setFormData] = useState({
    bridgeName: "",
    bridgeTypeId: "",
    notes: "",
    tags: "",
    isActive: true,
    levelLabels: {
      level1: {
        label: "Frame",
        enabled: true,
        holdsAreaData: false,
        holdsExposureClassData: false,
        isRequired: true,
      },
    },
    conditionRatingScale: null,
  });
  const [confirmationDialog, setConfirmationDialog] = useState(null);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const [availableScales, setAvailableScales] = useState({});
  const [showScaleCustomization, setShowScaleCustomization] = useState(false);

  useEffect(() => {
    fetchBridges();
    fetchAvailableScales();
  }, [pagination.currentPage, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAvailableScales = async () => {
    try {
      const response = await api.get("/bridges/condition-rating-scales");
      if (response.data.success) {
        setAvailableScales(response.data.data || {});
      }
    } catch (err) {
      console.error("Error fetching condition rating scales:", err);
    }
  };

  useEffect(() => {
    fetchBridgeTypes();
  }, []);

  const fetchBridges = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        page: pagination.currentPage.toString(),
        limit: "50",
      });

      if (filters.search) params.append("search", filters.search);
      if (filters.isActive !== "") params.append("isActive", filters.isActive);

      const response = await api.get(`/bridges?${params}`);

      if (response.data.success) {
        setBridges(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError("Failed to fetch bridges");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch bridges");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const resetForm = () => {
    setFormData({
      bridgeName: "",
      bridgeTypeId: "",
      notes: "",
      tags: "",
      isActive: true,
      levelLabels: {
        level1: {
          label: "Frame",
          enabled: true,
          holdsAreaData: false,
          holdsExposureClassData: false,
          isRequired: true,
        },
      },
      conditionRatingScale: null,
    });
    setShowScaleCustomization(false);
    setEditingBridge(null);
  };

  const addLevel = () => {
    const levelKeys = Object.keys(formData.levelLabels);
    const nextLevelNum = levelKeys.length + 1;
    const levelKey = `level${nextLevelNum}`;

    setFormData((prev) => ({
      ...prev,
      levelLabels: {
        ...prev.levelLabels,
        [levelKey]: {
          label: `Level ${nextLevelNum}`,
          enabled: true, // Default to enabled
          holdsAreaData: false,
          holdsExposureClassData: false,
        },
      },
    }));
  };

  const removeLevel = (levelNum) => {
    const levelKey = `level${levelNum}`;
    const levelKeys = Object.keys(formData.levelLabels);
    const level = formData.levelLabels[levelKey];

    // Don't allow removing if it's the only level
    if (levelKeys.length <= 1) {
      alert(
        "Cannot remove the last level. A bridge must have at least one level.",
      );
      return;
    }

    // Don't allow removing Level 1 (Frame) - it's required for organizing spans by direction/position
    if (levelNum === 1 || level?.isRequired) {
      alert(
        "Cannot remove Frame (Level 1). Frame is required for organizing spans by direction and position.",
      );
      return;
    }

    // Check if level has data (this would need backend validation, but we can warn)
    if (level && level.enabled) {
      if (
        !window.confirm(
          `Are you sure you want to remove Level ${levelNum}? This action cannot be undone if the level has data.`,
        )
      ) {
        return;
      }
    }

    const newLevelLabels = { ...formData.levelLabels };
    delete newLevelLabels[levelKey];

    setFormData((prev) => ({
      ...prev,
      levelLabels: newLevelLabels,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Prepare level labels and enabled levels for API
      const levelLabels = {};
      const enabledLevels = [];
      let hasAreaData = false;

      // Validate: at least one level must have "Area Data" marked
      Object.keys(formData.levelLabels).forEach((levelKey) => {
        const level = formData.levelLabels[levelKey];
        if (level.holdsAreaData) {
          hasAreaData = true;
        }
      });

      if (!hasAreaData) {
        alert(
          'At least one level must have "Area Data" marked. Please select which level holds the area data.',
        );
        setLoading(false);
        return;
      }

      // Process all levels dynamically
      Object.keys(formData.levelLabels)
        .sort()
        .forEach((levelKey) => {
          const level = formData.levelLabels[levelKey];
          const levelNum = parseInt(levelKey.replace("level", ""));
          // Ensure Level 1 (Frame) is always required
          const isRequired = levelKey === "level1" || level.isRequired || false;
          levelLabels[levelKey] = {
            label: level.label,
            enabled: level.enabled,
            holdsAreaData: level.holdsAreaData || false,
            holdsExposureClassData: level.holdsExposureClassData || false,
            isRequired: isRequired,
          };
          if (level.enabled) {
            enabledLevels.push(levelNum);
          }
        });

      // Ensure Level 1 (Frame) always exists and is enabled
      if (!levelLabels.level1) {
        levelLabels.level1 = {
          label: "Frame",
          enabled: true,
          holdsAreaData: false,
          holdsExposureClassData: false,
          isRequired: true,
        };
        enabledLevels.push(1);
      } else {
        levelLabels.level1.isRequired = true;
        // Ensure Frame is enabled
        if (!levelLabels.level1.enabled) {
          levelLabels.level1.enabled = true;
          if (!enabledLevels.includes(1)) {
            enabledLevels.push(1);
          }
        }
      }

      const submitData = {
        ...formData,
        levelLabels: levelLabels,
        enabledLevels: enabledLevels,
      };

      // Keep conditionRatingScale in submitData (even if null) so backend can clear it

      if (editingBridge) {
        const response = await api.put(
          `/bridges/${editingBridge.id}`,
          submitData,
        );
        if (response.data.requiresConfirmation) {
          // Show confirmation dialog
          setConfirmationDialog({
            message: response.data.message,
            details: response.data.confirmationDetails,
          });
          setPendingSubmitData(submitData);
          setLoading(false);
          return;
        }
        if (response.data.success) {
          setShowModal(false);
          resetForm();
          fetchBridges();
          // If we're on the bridge detail page, refresh it
          if (
            window.location.pathname.includes(`/bridges/${editingBridge.id}`)
          ) {
            window.location.reload();
          }
        } else {
          setError(response.data.message || "Failed to update bridge");
        }
      } else {
        const response = await api.post("/bridges", submitData);
        if (response.data.success) {
          setShowModal(false);
          resetForm();
          fetchBridges();
        } else {
          setError(response.data.message || "Failed to create bridge");
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save bridge");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAreaDataClear = async () => {
    if (!pendingSubmitData || !editingBridge) return;

    setLoading(true);
    setError("");

    try {
      // Resend the request with confirmation flag
      const submitDataWithConfirmation = {
        ...pendingSubmitData,
        confirmAreaDataClear: true,
      };

      const response = await api.put(
        `/bridges/${editingBridge.id}`,
        submitDataWithConfirmation,
      );

      if (response.data.success) {
        setShowModal(false);
        setConfirmationDialog(null);
        setPendingSubmitData(null);
        resetForm();
        fetchBridges();
        // If we're on the bridge detail page, refresh it
        if (window.location.pathname.includes(`/bridges/${editingBridge.id}`)) {
          window.location.reload();
        }
      } else {
        setError(response.data.message || "Failed to update bridge");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save bridge");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAreaDataClear = () => {
    setConfirmationDialog(null);
    setPendingSubmitData(null);
  };

  const handleEdit = (bridge) => {
    setEditingBridge(bridge);

    // Parse levelLabels if it's a string (JSON)
    let parsedLevelLabels = null;
    if (bridge.levelLabels) {
      if (typeof bridge.levelLabels === "string") {
        try {
          parsedLevelLabels = JSON.parse(bridge.levelLabels);
        } catch (e) {
          console.error("Error parsing levelLabels:", e);
          parsedLevelLabels = null;
        }
      } else {
        parsedLevelLabels = bridge.levelLabels;
      }
    }

    // Use parsed levels or default to one level (Frame)
    let levelLabels = {
      level1: {
        label: "Frame",
        enabled: true,
        holdsAreaData: false,
        holdsExposureClassData: false,
        isRequired: true,
      },
    };

    if (parsedLevelLabels && Object.keys(parsedLevelLabels).length > 0) {
      // Load all levels from bridge data
      levelLabels = {};
      Object.keys(parsedLevelLabels)
        .sort()
        .forEach((levelKey) => {
          const level = parsedLevelLabels[levelKey];
          // Level 1 (Frame) is always required
          const isRequired = levelKey === "level1" || level.isRequired || false;
          levelLabels[levelKey] = {
            label: level.label || `Level ${levelKey.replace("level", "")}`,
            enabled: level.enabled !== false,
            holdsAreaData: level.holdsAreaData || false,
            holdsExposureClassData: level.holdsExposureClassData || false,
            isRequired: isRequired,
          };
        });

      // Ensure Level 1 exists and is marked as required
      if (!levelLabels.level1) {
        levelLabels.level1 = {
          label: "Frame",
          enabled: true,
          holdsAreaData: false,
          holdsExposureClassData: false,
          isRequired: true,
        };
      } else {
        levelLabels.level1.isRequired = true;
      }
    }

    // Parse conditionRatingScale if it's a string
    let parsedConditionScale = null;
    if (bridge.conditionRatingScale || bridge.condition_rating_scale) {
      const scaleData =
        bridge.conditionRatingScale || bridge.condition_rating_scale;
      if (typeof scaleData === "string") {
        try {
          parsedConditionScale = JSON.parse(scaleData);
        } catch (e) {
          console.error("Error parsing conditionRatingScale:", e);
          parsedConditionScale = null;
        }
      } else {
        parsedConditionScale = scaleData;
      }
    }

    setFormData({
      bridgeName: bridge.bridgeName,
      bridgeTypeId: bridge.bridgeTypeId || "",
      notes: bridge.notes || "",
      tags: bridge.tags || "",
      isActive: bridge.isActive,
      levelLabels: levelLabels,
      conditionRatingScale: parsedConditionScale,
    });
    setShowScaleCustomization(!!parsedConditionScale);
    setShowModal(true);
  };

  const handleDelete = async (bridge) => {
    if (
      window.confirm(
        `Are you sure you want to delete bridge "${bridge.bridgeName}"?`,
      )
    ) {
      try {
        const response = await api.delete(`/bridges/${bridge.id}`);
        if (response.data.success) {
          fetchBridges();
        } else {
          setError(response.data.message || "Failed to delete bridge");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to delete bridge");
      }
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  };

  const handleToggleStatus = async (bridgeId) => {
    if (isPlanner || togglingBridgeId) {
      return;
    }

    setTogglingBridgeId(bridgeId);
    setError("");

    try {
      const response = await api.patch(`/bridges/${bridgeId}/toggle-status`);
      if (response.data.success) {
        setBridges((prev) =>
          prev.map((bridge) =>
            bridge.id === bridgeId
              ? { ...bridge, isActive: !bridge.isActive }
              : bridge,
          ),
        );
      } else {
        setError(response.data.message || "Failed to toggle bridge status");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to toggle bridge status");
    } finally {
      setTogglingBridgeId(null);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      isActive: "",
    });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const fetchBridgeTypes = async () => {
    try {
      const response = await api.get("/bridge-types");
      setBridgeTypes(response.data.data || response.data);
    } catch (error) {
      console.error("Error fetching bridge types:", error);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start gap-2">
        <p className="text-gray-600">Manage bridge inventory and information</p>
        <InlineFieldTooltip
          page="bridges"
          section="page_description"
          fieldKey="bridges_overview"
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-64">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <div className="relative">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search by bridge name, notes, or tags..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="min-w-32">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="isActive"
              value={filters.isActive}
              onChange={handleFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <button
            onClick={clearFilters}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <FontAwesomeIcon icon={faFilter} className="mr-2" />
            Clear Filters
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-3">
          {!isPlanner && (
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Add Bridge
            </button>
          )}
          {isPlanner && (
            <p className="text-sm text-gray-500">
              Planner: view only. Edit/delete are disabled.
            </p>
          )}
        </div>

        <div className="text-sm text-gray-600">
          Showing {bridges.length} of {pagination.totalBridges} bridges
        </div>
      </div>

      {/* Bridges Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading bridges...</p>
          </div>
        ) : bridges.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FontAwesomeIcon icon={faBuilding} className="text-4xl mb-4" />
            <p>No bridges found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bridge Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bridges.map((bridge) => (
                  <tr key={bridge.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => navigate(`/bridges/${bridge.id}`)}
                        className="text-sm font-medium text-gray-900 hover:text-blue-700 hover:underline"
                        title="View Details"
                      >
                        {bridge.bridgeName}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={bridge.isActive}
                        aria-label={`Set ${bridge.bridgeName} status`}
                        disabled={isPlanner || togglingBridgeId === bridge.id}
                        onClick={() => handleToggleStatus(bridge.id)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          bridge.isActive ? "bg-blue-600" : "bg-gray-300"
                        } ${isPlanner ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            bridge.isActive ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => navigate(`/bridges/${bridge.id}`)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
                          title="View Details"
                        >
                          <FontAwesomeIcon icon={faEye} className="h-4 w-4" />
                        </button>
                        {!isPlanner && (
                          <>
                            <button
                              onClick={() => handleEdit(bridge)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
                              title="Edit Bridge"
                            >
                              <FontAwesomeIcon
                                icon={faPencil}
                                className="h-4 w-4"
                              />
                            </button>
                            <button
                              onClick={() => handleDelete(bridge)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200"
                              title="Delete Bridge"
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                className="h-4 w-4"
                              />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Page {pagination.currentPage} of {pagination.totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNextPage}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-4">
                <span>{editingBridge ? "Edit Bridge" : "Add New Bridge"}</span>
                <InlineFieldTooltip
                  page="bridges"
                  section="modal_titles"
                  fieldKey="bridge_form"
                  fallbackText="Create or edit bridge details and configuration."
                  title="Bridge Form"
                />
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bridge Name *
                    </label>
                    <input
                      type="text"
                      name="bridgeName"
                      value={formData.bridgeName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., Main Street Bridge"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bridge Type *
                    </label>
                    <select
                      name="bridgeTypeId"
                      value={formData.bridgeTypeId}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Bridge Type</option>
                      {bridgeTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.bridgeTypeName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Additional notes about the bridge..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tags
                  </label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., arterial,steel,urban"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Active
                  </label>
                </div>

                {/* Level Labels Configuration */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Bridge Structure Levels Configuration
                  </h4>
                  <p className="text-xs text-gray-500 mb-4">
                    Configure the hierarchy level labels and enable/disable
                    levels for this bridge.
                  </p>

                  <div className="space-y-3">
                    {Object.keys(formData.levelLabels)
                      .sort()
                      .map((levelKey) => {
                        const levelNum = parseInt(
                          levelKey.replace("level", ""),
                        );
                        const level = formData.levelLabels[levelKey];

                        return (
                          <div
                            key={levelKey}
                            className="border border-gray-200 rounded-md p-3 bg-gray-50"
                          >
                            <div className="flex items-center gap-3 mb-2">
                              <input
                                type="checkbox"
                                checked={level.enabled}
                                disabled={level.isRequired} // Frame (Level 1) cannot be disabled
                                onChange={(e) => {
                                  if (level.isRequired) {
                                    alert(
                                      "Frame (Level 1) must always be enabled as it is required for organizing spans by direction and position.",
                                    );
                                    return;
                                  }
                                  setFormData((prev) => ({
                                    ...prev,
                                    levelLabels: {
                                      ...prev.levelLabels,
                                      [levelKey]: {
                                        ...prev.levelLabels[levelKey],
                                        enabled: e.target.checked,
                                      },
                                    },
                                  }));
                                }}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label className="min-w-20 text-sm font-medium text-gray-700">
                                Level {levelNum}:
                              </label>
                              <input
                                type="text"
                                value={level.label || ""}
                                onChange={(e) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    levelLabels: {
                                      ...prev.levelLabels,
                                      [levelKey]: {
                                        ...prev.levelLabels[levelKey],
                                        label: e.target.value,
                                      },
                                    },
                                  }));
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                placeholder={`Level ${levelNum} label`}
                              />
                              {Object.keys(formData.levelLabels).length > 1 &&
                                !level.isRequired && (
                                  <button
                                    type="button"
                                    onClick={() => removeLevel(levelNum)}
                                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                                    title="Remove this level"
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                )}
                              {level.isRequired && (
                                <span
                                  className="text-xs text-gray-500 italic"
                                  title="This level is required and cannot be removed"
                                >
                                  (Required)
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 ml-8 text-xs">
                              <label className="flex items-center gap-1 text-gray-600">
                                <input
                                  type="checkbox"
                                  checked={level.holdsAreaData || false}
                                  disabled={(() => {
                                    // Disable if another level already has area data
                                    const otherLevelHasAreaData = Object.keys(
                                      formData.levelLabels,
                                    ).some(
                                      (key) =>
                                        key !== levelKey &&
                                        formData.levelLabels[key].holdsAreaData,
                                    );
                                    return (
                                      otherLevelHasAreaData &&
                                      !level.holdsAreaData
                                    );
                                  })()}
                                  onChange={(e) => {
                                    // Only one level can hold area data
                                    const newLevelLabels = {
                                      ...formData.levelLabels,
                                    };
                                    Object.keys(newLevelLabels).forEach(
                                      (key) => {
                                        if (key !== levelKey) {
                                          newLevelLabels[key].holdsAreaData =
                                            false;
                                        }
                                      },
                                    );
                                    newLevelLabels[levelKey].holdsAreaData =
                                      e.target.checked;
                                    setFormData((prev) => ({
                                      ...prev,
                                      levelLabels: newLevelLabels,
                                    }));
                                  }}
                                  className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={(() => {
                                    const otherLevelHasAreaData = Object.keys(
                                      formData.levelLabels,
                                    ).some(
                                      (key) =>
                                        key !== levelKey &&
                                        formData.levelLabels[key].holdsAreaData,
                                    );
                                    if (
                                      otherLevelHasAreaData &&
                                      !level.holdsAreaData
                                    ) {
                                      return "Only one level can hold area data. Uncheck the other level first.";
                                    }
                                    return "Mark this level as holding area data. Only one level can hold area data per bridge.";
                                  })()}
                                />
                                <span
                                  className={(() => {
                                    const otherLevelHasAreaData = Object.keys(
                                      formData.levelLabels,
                                    ).some(
                                      (key) =>
                                        key !== levelKey &&
                                        formData.levelLabels[key].holdsAreaData,
                                    );
                                    return otherLevelHasAreaData &&
                                      !level.holdsAreaData
                                      ? "text-gray-400"
                                      : "";
                                  })()}
                                >
                                  Holds Area Data
                                </span>
                              </label>
                              {(() => {
                                const otherLevelHasAreaData = Object.keys(
                                  formData.levelLabels,
                                ).some(
                                  (key) =>
                                    key !== levelKey &&
                                    formData.levelLabels[key].holdsAreaData,
                                );
                                if (
                                  otherLevelHasAreaData &&
                                  !level.holdsAreaData
                                ) {
                                  return (
                                    <span
                                      className="text-xs text-amber-600 italic"
                                      title="Another level already holds area data"
                                    >
                                      (Another level selected)
                                    </span>
                                  );
                                }
                                return null;
                              })()}
                              <label className="flex items-center gap-1 text-gray-600">
                                <input
                                  type="checkbox"
                                  checked={
                                    level.holdsExposureClassData || false
                                  }
                                  onChange={(e) => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      levelLabels: {
                                        ...prev.levelLabels,
                                        [levelKey]: {
                                          ...prev.levelLabels[levelKey],
                                          holdsExposureClassData:
                                            e.target.checked,
                                        },
                                      },
                                    }));
                                  }}
                                  className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="flex items-center gap-1">
                                  Holds Exposure Class Data
                                  <span
                                    className="text-gray-400 cursor-help"
                                    title="Exposure class (Corrosion-Damage Combination) is stored at the element level, not the entire bridge. Different elements can have different exposure classes based on their location and environment. For example, elements on the north side might have different corrosion exposure than elements on the south side."
                                  >
                                    <FontAwesomeIcon icon={faQuestionCircle} />
                                  </span>
                                </span>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    <button
                      type="button"
                      onClick={addLevel}
                      className="w-full mt-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center gap-2"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      Add Level
                    </button>
                  </div>
                </div>

                {/* Condition Rating Scale Configuration */}
                <div className="border-t border-gray-200 pt-6 mt-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">
                    Condition Rating Scale Configuration
                  </h4>
                  <p className="text-xs text-gray-500 mb-4">
                    Configure the condition rating scale for displaying bridge
                    status at exposure class level. Select a default template or
                    create a custom scale.
                  </p>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Scale Template
                      </label>
                      <select
                        value={formData.conditionRatingScale?.scaleName || ""}
                        onChange={(e) => {
                          const selectedScale = e.target.value;
                          if (selectedScale && availableScales[selectedScale]) {
                            const scale = availableScales[selectedScale];
                            setFormData((prev) => ({
                              ...prev,
                              conditionRatingScale: {
                                scaleName: selectedScale,
                                name: scale.name,
                                description: scale.description,
                                levels: scale.levels,
                              },
                            }));
                            setShowScaleCustomization(false);
                          } else {
                            // Explicitly set to null when "No Scale" is selected
                            setFormData((prev) => ({
                              ...prev,
                              conditionRatingScale: null,
                            }));
                            setShowScaleCustomization(false);
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      >
                        <option value="">No Scale (Disabled)</option>
                        {Object.keys(availableScales).map((scaleKey) => (
                          <option key={scaleKey} value={scaleKey}>
                            {availableScales[scaleKey].name} -{" "}
                            {availableScales[scaleKey].description}
                          </option>
                        ))}
                      </select>
                    </div>

                    {formData.conditionRatingScale && (
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-blue-900">
                            {formData.conditionRatingScale.name ||
                              formData.conditionRatingScale.scaleName}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setShowScaleCustomization(!showScaleCustomization)
                            }
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            {showScaleCustomization
                              ? "Hide Details"
                              : "Customize"}
                          </button>
                        </div>
                        {showScaleCustomization &&
                          formData.conditionRatingScale.levels && (
                            <div className="mt-3 space-y-2">
                              <div className="text-xs font-medium text-gray-700 mb-2">
                                Scale Levels:
                              </div>
                              {formData.conditionRatingScale.levels.map(
                                (level, idx) => (
                                  <div
                                    key={idx}
                                    className="flex items-center gap-2 text-xs bg-white p-2 rounded border border-gray-200"
                                  >
                                    <div
                                      className="w-4 h-4 rounded border border-gray-300"
                                      style={{ backgroundColor: level.color }}
                                      title={level.color}
                                    />
                                    <span className="font-medium">
                                      {level.label}:
                                    </span>
                                    <span className="text-gray-600">
                                      {level.name}
                                    </span>
                                    <span className="text-gray-500 ml-auto">
                                      {level.min}% - {level.max}%
                                    </span>
                                  </div>
                                ),
                              )}
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {loading
                      ? "Saving..."
                      : editingBridge
                        ? "Update"
                        : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Area Data Clear Confirmation Dialog */}
      {confirmationDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon
                    icon={faQuestionCircle}
                    className="text-amber-600 text-xl"
                  />
                </div>
                <h3 className="ml-3 flex items-center gap-2 text-lg font-medium text-gray-900">
                  <span>Confirm Area Data Level Change</span>
                  <InlineFieldTooltip
                    page="bridges"
                    section="modal_titles"
                    fieldKey="confirm_area_data_level_change"
                    fallbackText="Confirms the impact of changing which hierarchy level stores area data."
                    title="Confirm Area Data Level Change"
                  />
                </h3>
              </div>
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-4">
                  {confirmationDialog.message}
                </p>
                {confirmationDialog.details && (
                  <div className="bg-gray-50 p-3 rounded-md mb-4">
                    <p className="text-xs text-gray-600 mb-1">
                      <strong>Affected Elements:</strong>{" "}
                      {confirmationDialog.details.affectedElementsCount}{" "}
                      element(s) at {confirmationDialog.details.oldLevelLabel}{" "}
                      (Level {confirmationDialog.details.oldLevel})
                    </p>
                    <p className="text-xs text-gray-600">
                      <strong>New Area Data Level:</strong>{" "}
                      {confirmationDialog.details.newLevelLabel} (Level{" "}
                      {confirmationDialog.details.newLevel})
                    </p>
                  </div>
                )}
                <p className="text-sm font-medium text-red-600">
                  ⚠️ This action cannot be undone.
                </p>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancelAreaDataClear}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmAreaDataClear}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {loading ? "Processing..." : "Yes, Clear Area Data"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bridges;
